<template>
  <div class="sc-payment-container">
    <h1 class="page-title">
      <b>{{ $t('payment_page.payment') }}</b> {{ $t('payment_page.settings') }}
    </h1>
    <div>
      <ScCard class="payment-cart-content payment-cart">
        <div slot="title" class="title">
          {{ $t('payment_page.cart') }}
        </div>
        <div slot="content" class="content">
          <LoyaltyChipSelector id="loyalty-select" :values="possibleLoyalties" />
          <div class="cart-wrapper">
            <cart-table></cart-table>
          </div>
        </div>
      </ScCard>
    </div>
    <div>
      <ScCard class="payment-plan payment-cart">
        <div slot="title" class="title">
          {{ $t('payment_page.payment_plan.pick_payment_plan') }}
        </div>
        <div slot="content" class="content">
          <div>
            <ScRadioInput class="radio-element">
              <input type="radio" id="one" name="selector" v-model="billingCycle" :value="1">
              <label for="one">{{ $t('payment_page.payment_plan.monthly_payment') }}</label>
            </ScRadioInput>
            <ScRadioInput class="radio-element">
              <input type="radio" id="two" name="selector" v-model="billingCycle" :value="12">
              <label for="two">{{ $t('payment_page.payment_plan.yearly_payment') }}
                <p class="label-info">{{ $t('payment_page.payment_plan.promo_yearly') }} <span class="text-highlight">{{ $t('payment_page.payment_plan.yearly_sum', { sum: $price(getYearlyDiscountAmount()) }) }}</span></p>
              </label>
            </ScRadioInput>
          </div>
        </div>
      </ScCard>
    </div>
    <div>
      <ScCard class="payment-method payment-cart">
        <div slot="title" class="title">
          {{ $t('payment_page.payment_method.pick_payment_method') }}
        </div>
        <div slot="content" class="content">
          <div>
            <ScRadioInput class="radio-element">
              <input
                type="radio"
                :value="PaymentMethods.OTP"
                id="card"
                name="paymentMethod"
                v-model="userData.paymentMethod"
                v-validate="{ required: true }"
                @click="resetCheckboxes(PaymentMethods.OTP)">
              <label for="card" :class="validationClasses('paymentMethod')">
                {{ $t('payment_page.payment_method.credit_card') }}
                <a :href="$t('payment_page.payment_method.simple_pay_payment_info_url')" target="_blank">
                  <img height="27" src="./../../../public/img/simplepay_horizontal_02.png" :title="$t('payment.simple_pay.logo.title')" :alt="$t('payment.simple_pay.logo.alt')">
                </a>
              </label>
            </ScRadioInput>
            <ScRadioInput class="radio-element">
              <input
                type="radio"
                :value="PaymentMethods.WIRE"
                id="wire"
                name="paymentMethod"
                v-validate="{ required: true }"
                v-model="userData.paymentMethod"
                @click="resetCheckboxes(PaymentMethods.WIRE)">
              <label for="wire" :class="validationClasses('paymentMethod')">{{ $t('payment_page.payment_method.wire') }}
                <p class="label-info">{{ $t('payment_page.payment_method.info_for_wire') }}</p>
              </label>
            </ScRadioInput>
          </div>
          <div>
            <span class="validation-error">{{ errors.first('paymentMethod') }}</span>
          </div>
        </div>
      </ScCard>
    </div>
    <div>
      <sc-card class="sc-registration-info">
        <div slot="title" class="sc-card-subtitle">{{ $t('contract_inputs.register_title')}}</div>
        <div slot="content" class="content">
        <div class="reg-info">{{ $t('contract_inputs.register_info')}}</div>
          <div class="left-side">
            <sc-form-input id="username-input">
              <label class="label-w-tooltip" for="username">{{ $t('contract_inputs.username')}} <sc-tooltip :icon="'info_outline'" class="sc-tooltip" :text="$t('contract_inputs.username_tooltip')"/></label>
              <input disabled type="text" id="username" name="username" v-model="username" v-validate="{ required: true, max: 255, validEmail: true }" :placeholder=" $t('contract_inputs.username_placeholder')" maxlength="255"/>
              <span class="accepted-input"><i v-if="!errors.has('username') && fields.username && fields.username.valid" class="sc-icon-check"></i><i v-if="errors.has('username')" class="sc-icon-cancel"></i></span>
              <span class="validation-error">{{ errors.first('username') }}</span>
            </sc-form-input>
            <sc-form-input>
              <label for="password1">{{ $t('contract_inputs.password')}}</label>
              <input type="password" id="password1" name="password1" v-model="userData.password1" v-validate="{required: true, min: 8, validPassword: true}" ref="password1"/>
              <span class="icons">
                  <span class="password-eye"><i class="sc-icon-eye" @click="togglePasswordVisibility(1)" :class="passwordAcceptable? 'pass-ok' : ''" /></span>
                  <span class="accepted-input"><i v-if="!errors.has('password1') && fields.password1 && fields.password1.valid" class="sc-icon-check"></i><i v-if="errors.has('password1')" class="sc-icon-cancel"></i></span>
                </span>
            </sc-form-input>
            <sc-form-input>
              <label for="password2">{{ $t('contract_inputs.password_again')}}</label>
              <input type="password" id="password2" name="password2" v-model="userData.password2" v-validate="'required|confirmed:password1'" ref="password2"/>
              <span class="icons">
                  <span class="password-eye"><i class="sc-icon-eye" :class="passwordAcceptable? 'pass-ok' : ''" @click="togglePasswordVisibility(2)" /></span>
                  <span class="accepted-input"><i v-if="!errors.has('password2') && fields.password2 && fields.password2.valid" class="sc-icon-check"></i><i v-if="errors.has('password2')" class="sc-icon-cancel"></i></span>
                </span>
            </sc-form-input>
            <div class="criteria-container">
              <div :class="passMin8Char? 'pass-ok' : 'opaque'"><span class="accepted"><i v-if="!passwordAcceptable && !passMin8Char" class="sc-icon-cancel"/><i v-else class="sc-icon-check"/></span> {{ $t('contract_inputs.password_8_char')}}</div>
              <div :class="passContainsLowerAndUpperCaseLetters? 'pass-ok' : 'opaque'"><span class="accepted"><i v-if="!passwordAcceptable && !passContainsLowerAndUpperCaseLetters" class="sc-icon-cancel"/><i v-else class="sc-icon-check"/></span> {{ $t('contract_inputs.password_lower_and_upper')}}</div>
              <div :class="passesEqual? 'pass-ok' : 'opaque'"><span class="accepted"><i v-if="!passwordAcceptable && !passesEqual" class="sc-icon-cancel"/><i v-else class="sc-icon-check"/></span> {{ $t('contract_inputs.password_same')}}</div>
            </div>

            <div class="checkboxes">
              <sc-checkbox>
                <input
                  type="checkbox"
                  id="terms-accept"
                  name="terms-accept"
                  v-model="tempTermsAccept"
                  v-validate="{ required: true }"
                  @click="onClickConditions(ContractConditionTypes.USER_TERMS)">
                <i18n
                  path="payment_page.accept_conditions"
                  tag="label"
                  for="terms-accept"
                  class="checkbox-label"
                  :places="{ read: $t('payment_page.read_and') }">
                  <a place="what" :href="$t('payment_page.terms_and_conds_url')" target="_blank">{{ $t('payment_page.terms') }}</a>
                </i18n>
              </sc-checkbox>

              <sc-checkbox>
                <input id="contract-accept" name="contract-accept" type="checkbox" v-model="userData.contractAccept" v-validate="{ required: true }" />
                <label for="contract-accept" class="checkbox-label">
                  <p>
                    {{ $t('payment_page.terms_ticker') }}
                    <a :href="$t('payment_page.contract_sample_url')" target="_blank">{{ $t('payment_page.terms_and_conds') }}</a>
                  </p>
                </label>
              </sc-checkbox>

              <sc-checkbox v-if="isCardPayment()">
                <input
                  type="checkbox"
                  id="otp-accept"
                  name="otp-accept"
                  v-model="userData.otpAccept"
                  v-validate="{ required: true }">
                <i18n
                  path="payment_page.accept_conditions"
                  tag="label"
                  for="otp-accept"
                  class="checkbox-label"
                  :places="{ read: $t('payment_page.read_and') }">
                  <a
                    place="what"
                    class="sc-payment-otp-data-passing-open"
                    @click="onClickConditions(ContractConditionTypes.OTP_DATA_PASSING)">
                    {{ $t('payment_page.otp_data_passing') }}
                  </a>
                </i18n>
              </sc-checkbox>

              <span class="validation-error">{{
                errors.first('terms-accept') ||
                errors.first('contract-accept') ||
                errors.first('otp-accept')}}
              </span>
            </div>

        </div>
        </div>
      </sc-card>
    </div>
    <sc-card v-if="longValidationErrors" class="errors-container">
      <div slot="content" class="sc-content-container">
        <ul>
          <li class="long-validation-errors" v-for="error in longValidationErrors" v-bind:key="error">{{ $t(error) }}</li>
        </ul>
      </div>
    </sc-card>
    <div>
      <div class="error-message text-center" v-if="error">{{ $t('payment.order_error.' + error) }}</div>
      <div class="payment-button-wrapper">
        <div class="payment-back-button">
          <rounded-button color="outline" @click="onBack">
            <span>{{ $t('common.back') }}</span>
          </rounded-button>
        </div>
        <div class="payment-order-button">
          <rounded-button color="primary" @click="onOrder">
            <span>{{ $t('common.order') }}</span>
          </rounded-button>
        </div>
      </div>
    </div>

    <div
      v-if="showConditions"
      class="payment-conditions-popup"
      :style="{ top: contractPopupTop + 'px', left: contractPopupLeft + 'px' }">
        <ApplyConditions
          :selectedCondition="selectedCondition"
          v-on:[selectedContractConditionAction]="catchSelected($event)"
          v-on:[closeConditions]="showConditions = false"/>
    </div>

  </div>
</template>

<script>
import { ScCard, ScRadioInput, ScCheckbox, ScFormInput, ScTooltip } from 'smartcommerce-component-library'
import { Routes } from '../utils/Routes'
import { OrderWizardMutationTypes as mutations } from '../store/mutation-types'
import LoyaltyChipSelector from './../components/packageselector/LoyaltyChipSelector'
import ApplyConditions from './../components/ApplyConditions'
import { ContractConditionActions } from './../utils/ContractConditionActions'
import { ContractConditionTypes } from './../utils/ContractConditionTypes'
import { PaymentMethods } from '@/shared/utils/PaymentMethods'
import validationMixin from '../../shared/mixins/validationMixin'
import CartTable from '../../shared/components/CartTable'
import productConfigMixin from '../../shared/mixins/productConfigMixin'
import { Constants } from '../utils/Constants'
import BusyService from '../services/BusyService'
import { OrderWizardActionTypes as actions } from '../store/action-types'
import RoundedButton from '../../shared/components/RoundedButton'

const ERRORS_TO_HANDLE = ['client: EXISTING_CLIENT']

export default {
  name: 'Payment',
  mixins: [validationMixin, productConfigMixin],
  components: {
    ScCard,
    ScRadioInput,
    ScCheckbox,
    ScFormInput,
    ScTooltip,
    LoyaltyChipSelector,
    ApplyConditions,
    CartTable,
    RoundedButton
  },
  data () {
    return {
      selectedContractConditionAction: ContractConditionActions.SELECTED,
      closeConditions: ContractConditionActions.CLOSE,
      ContractConditionTypes,
      PaymentMethods,
      Routes,
      contractPopupTop: null,
      contractPopupLeft: null,
      showConditions: false,
      selectedCondition: null,
      error: null,
      longValidationErrors: null,
      tempTermsAccept: false
    }
  },
  created () {
    if (this.billingCycle !== 1 && this.billingCycle !== 12) this.billingCycle = 12
  },
  methods: {
    onClickConditions (conditionType) {
      if (conditionType === ContractConditionTypes.USER_TERMS && this.userData.termsAccept) {
        this.tempTermsAccept = false
        this.userData.termsAccept = false
        return
      }
      if (conditionType === ContractConditionTypes.OTP_DATA_PASSING) {
        this.userData.otpAccept = !this.userData.otpAccept
      }
      this.selectedCondition = conditionType
      this.contractPopupTop = window.pageYOffset + 30
      this.contractPopupLeft = (window.innerWidth - 700) / 2
      this.showConditions = true
    },
    catchSelected (event) {
      this.tempTermsAccept = event
      this.userData.termsAccept = event
      this.showConditions = false
    },
    togglePasswordVisibility (pwNumber) {
      let input = pwNumber === 1 ? this.$refs.password1 : this.$refs.password2
      input.type = input.type === 'password' ? 'text' : 'password'
    },
    onOrder () {
      this.error = null
      this.longValidationErrors = null
      this.$validator.validateAll().then(async valid => {
        if (!valid) {
          this.longValidationErrors = this.collectLongErrorMessageKeys()
          return
        }

        this.$ga && this.$ga.event({
          eventCategory: 'Order or Purchase',
          eventAction: 'Payment'
        })

        const busyId = BusyService.busy()
        try {
          const response = await this.$store.dispatch(actions.POST_ORDER)
          let params = {
            invoice: response.invoice,
            orderId: response.orderId,
            payment: response.payment,
            paymentMethod: response.paymentMethod,
            contract: response.contract
          }
          this.$store.commit(mutations.SET_LAST_ORDER_RESPONSE, params)
          if (response.paymentMethod === PaymentMethods.OTP) {
            window.location = response.payment.bankcard.actionUrl
          } else {
            this.$store.dispatch(actions.POST_ORDER_CLEANUP)
            this.$router.push(Routes.THANK_YOU_WIRE)
          }
        } catch (e) {
          this.error = this.firstError(e.errors) || 'PAYMENT_ERROR'
          console.log(e)
        } finally {
          BusyService.finished(busyId)
        }
      })
    },
    firstError (errors) {
      if (!errors || !Array.isArray(errors)) return null
      for (let i = 0; i < errors.length; i++) {
        if (ERRORS_TO_HANDLE.indexOf(errors[i]) > -1) return errors[i]
      }
    },
    isCardPayment () {
      return this.userData.paymentMethod === PaymentMethods.OTP
    },
    resetCheckboxes (selected) {
      if (selected === PaymentMethods.WIRE) {
        this.userData.otpAccept = false
      }
    },
    onBack () {
      this.$router.push(Routes.CONTRACT_INPUT)
    },

    getYearlyDiscountAmount () {
      return this.$store.getters.getCartNetMonthlyFeesWithoutDiscount(12) * Constants.YEARLY_PAYMENT_DISCOUNT
    }
  },
  computed: {
    passMin8Char () {
      return this.userData.password1 !== undefined && this.userData.password1.length >= 8
    },
    passContainsLowerAndUpperCaseLetters () {
      return this.userData.password1 !== undefined && /[a-z]/.test(this.userData.password1) && /[A-Z]/.test(this.userData.password1)
    },
    passesEqual () {
      return this.userData.password1 !== undefined && this.userData.password2 !== null && (this.userData.password1 === this.userData.password2 && this.passMin8Char)
    },
    passwordAcceptable () {
      return (this.passMin8Char && this.passContainsLowerAndUpperCaseLetters && this.passesEqual)
    },

    billingCycle: {
      get () {
        return this.$store.state.billingCycleMonths
      },

      set (val) {
        this.$store.commit(mutations.UPDATE_BILLING_CYCLE, val)
      }
    },

    username: {
      get () {
        return this.$store.state.contractData.contactEmail
      }
    },

    userData: {
      get () {
        return this.$store.state.userData
      },

      set (val) {
        this.$store.commit(mutations.SET_USER_DATA, val)
      }
    }
  },

  mounted () {
    this.getProductConfig()
    this.tempTermsAccept = this.userData.termsAccept || false
  },

  watch: {
    userData: {
      handler: function () {
        this.$store.commit(mutations.SET_USER_DATA, this.userData)
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.payment-conditions-popup {
  position: absolute;
  background-color: white;
  width: 700px;
  height: 600px;
  z-index: 10001;
}
.sc-payment-opt-data-passing-open {
  color: $sc-primary;
  font-weight: 600;
  cursor: pointer;
}
.sc-payment-container {
  .title {
    font-size: 18px;
  }
  .payment-cart {
    width: 80%;
    margin-bottom: 36px;
    padding: 20px;
  }
  /deep/ .radio-element {
    label::before {
      top: 2px;
    }

    label.validated.invalid::before {
      border: 1px solid $sc-warn;
    }
  }
  .payment-cart-content {
    width: 100%;
    /deep/ .sc-chip-select-chip {
      font-size: 14px;
      width: 200px;
      padding: 2px 0;
      margin: 0 25px;
      transition: 0.3s;
    }
  }
  .payment-chip-select {
    margin-top: 37px;
    justify-content: space-evenly;
  }
  .payment-button-wrapper {
      display: flex;
      justify-content: space-between;
      flex-direction: row;
      width: 48%;
      margin: auto;
    .payment-order-button {
      width: 50%;
      button {
        margin-left: auto;
      }
    }
    .payment-back-button {
      width: 50%;
      align-self: flex-start;
    }
  }
  .label-info {
    font-size: 0.9rem;
  }

  #loyalty-select {
    margin: 30px auto;
    text-align: center;
  }

  /deep/ .sc-counter-value {
    color: $sc-text-dark !important;
  }
}

.sc-registration-info {
  margin-bottom: 36px;
  padding: 20px;
  width: 532px;

  .label-w-tooltip {
    display: flex;
    align-items: center;
  }

  .sc-tooltip {
    margin-left: 5px;
    padding-top: 5px;
    user-select: none;

    .sc-tooltip-icon {
      color: rgba(55, 71, 79, 0.3);
    }
  }

  .reg-info {
    text-align: justify;
    width: 430px;
  }
  .checkbox-label {
    display: block;
    font-size: 14px;
    font-weight: normal;
    font-style: normal;
    color: $sc-dim-text;
    line-height: 1;
  }

  .checkboxes {
    .checkbox-label {
      font-size: 16px;
      text-align: left;
      position: relative;
      padding-left: 30px;
      line-height: 1.29;

      a {
        font-weight: 600;
      }
    }

    input.is-invalid + label::before {
      border-color: $sc-invalid-input-color;
    }
  }

  .checkbox-label::before {
    width: 20px;
    height: 20px;
    position: absolute;
    left: 0px
  }

  #username-input {
    margin-top: 10px;
  }

  label {
    color: #455a64;
    font-size: 14px;
    font-weight: 500;

  }

  .criteria-container {
    color: $sc-dim-text;
    font-size: 14px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    line-height: 2;
    margin-bottom: 30px;
    margin-top: 10px;
  }

  .opaque {
    opacity: 0.6;
  }

  .pass-ok {

    .sc-icon-check {
      color: $sc-primary;
      font-weight: 900;
      text-shadow: 1px 0 $sc-primary;
    }
  }

  .icons {
    align-self: flex-end;
    width: 100px;
  }

  .sc-icon-eye {
    color: $sc-dim-text;
    opacity: 0.3;
    font-size: 25px;
    float: left;
    padding-right: 10px;
    position: relative;
    z-index: 2;
    margin-top: -41px;

    &.pass-ok {
      color: $primary;
      opacity: 1;
    }
  }

  .sc-card-subtitle {
    font-weight: $sc-card-title-font-weight;
    font-size: 16px;
    margin-bottom: 20px;
  }

  .accepted-input {
    align-self: flex-end;
    align-content: center;
    font-size: 24px;
    text-shadow: 1px 0 $sc-primary;
    color: $sc-primary;
    position: relative;
    float: right;
    height: 40px;
    width: 25px;
    margin-top: -41px;

    .sc-icon-cancel {
      color: $sc-invalid-input-color
    }

  }

  .sc-form-input {
    width: 480px;

    input {
      width: 430px;
      margin-right: 0;
    }
  }

  .is-invalid {
    border: solid 1px $sc-invalid-input-color;
  }

  .accepted {
    .sc-icon-cancel {
      color: $sc-invalid-input-color;
    }
  }
}

.cart-wrapper {
  margin-top: 36px;

  /deep/ .cart-container {
    .payment-sum {
      background: rgba(89,153,0,.08);
      border-bottom-color: rgba(89,153,0,.08);
    }

    th.payment-sum {
      border-bottom-color: rgba(89,153,0,.08);
    }

    tr td:nth-child(n+2), th:nth-child(n+2){
      text-align: center;
    }

    tr.spacer {
      border-top: 2px solid $primary;
    }

    tr.total-cost {
      font-weight: 800;
      line-height: 50px;
    }

    tr.monthly-cost {
      td:nth-child(5) {
        font-weight: 800;
      }
    }

    tr td:nth-child(6) {
      background: rgba(89, 153, 0, 0.08);
    }

    .stars-comment {
      text-align: center;
      margin-top: 15px;
    }
  }
}

.error-message {
  margin-bottom: 25px;
}

.errors-container {
  width: 532px;
  margin-bottom: 36px;
  background-color: $sc-validation-error-container-color;
  text-align: left;

  .sc-content-container {
    padding: 0 0 0 10px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      color: $sc-dim-text;
      font-size: 14px;
      line-height: 1.8;
    }

    li::before {
      content: "!";
      color: $warn;
      font-size: 18px;
      font-weight: 700;
      margin-right: 5px;
    }
  }
}

@media (max-width: 1200px) {
  .sc-payment-container {
    .payment-cart-content {
      /deep/ .sc-chip-select-chip {
        margin: 0 8px;
      }
    }
  }
}

.sc-payment-otp-data-passing-open:not([href]):not([tabindex]) {
  color: $sc-primary;
  text-decoration: none;
  background-color: transparent;

  &:hover {
    color: #2d4d00;
    text-decoration: underline;
  }
}

@media(max-width: 768px) {
  .page-title{
    margin: 15px 0px;
  }

  .payment-cart-content{
    padding: 20px 0px !important;

    .title{
      text-align: center;
    }
  }

  .sc-registration-info{
    width: 100%;
    padding: 20px 10px;

    .reg-info{
      width: 100%;
    }

    .sc-form-input{
      width: 100%;

      input{
        width: 90%;
      }

      .icons{
        width: 20%;
      }
    }
  }

  .payment-conditions-popup {
    width: 100%;
    left: 0px !important;
  }

  .sc-payment-container .payment-button-wrapper{
    width: 100%;

    .payment-back-button, .payment-order-button{
      width: 47%;
    }

    button{
      min-width: inherit;
      width: 100%;
    }
  }
}
</style>
